import { useMemo } from 'react';
import { useLovData } from 'hooks/useLovData';
import { PRODUCT_LOV_TYPES } from 'types/LOVTypes';
import { BaseMasterDataItem } from 'common/api/masterDataTypes';

export interface ProductLovHook {
  isReady: boolean;
  isLoading: boolean;
  isProcessing: boolean;
  error: string | null;
  retry: () => void;

  // Direct getters
  getProductStatuses: () => BaseMasterDataItem[];
  getProductTypes: () => BaseMasterDataItem[];
  getProductCategories: () => BaseMasterDataItem[];
  getProductSubCategories: () => BaseMasterDataItem[];
  getProductColors: () => BaseMasterDataItem[];
  getProductSizes: () => BaseMasterDataItem[];
  getProductBrands: () => BaseMasterDataItem[];
  getProductUOMs: () => BaseMasterDataItem[];
  getProductMarginTypes: () => BaseMasterDataItem[];

  // Hierarchical helpers
  getSubTypesForType: (typeRowId: number) => BaseMasterDataItem[];
  getSubCategoriesForCategory: (categoryRowId: number) => BaseMasterDataItem[];

  // Option arrays for dropdowns
  statusOptions: { value: number; label: string }[];
  typeOptions: { value: number; label: string }[];
  categoryOptions: { value: number; label: string }[];
  colorOptions: { value: number; label: string }[];
  sizeOptions: { value: number; label: string }[];
  brandOptions: { value: number; label: string }[];
  uomOptions: { value: number; label: string }[];
  marginTypeOptions: { value: number; label: string }[];
}

export const useProductLov = (): ProductLovHook => {
  const {
    processedData,
    isReady,
    isLoading,
    isProcessing,
    getByType,
    error,
    retry,
  } = useLovData();

  // Helper function to create unique options by name
  const createUniqueOptions = (
    items: BaseMasterDataItem[],
    optionType?: string,
    deduplicateBy: keyof BaseMasterDataItem = 'name'
  ) => {
    const originalCount = items.length;
    const uniqueItems = items.filter(
      (item, index, self) =>
        index ===
        self.findIndex((uom) => uom[deduplicateBy] === item[deduplicateBy])
    );
    const uniqueCount = uniqueItems.length;

    if (originalCount !== uniqueCount && optionType) {
      // console.log(
      //   `${optionType}: Removed ${
      //     originalCount - uniqueCount
      //   } duplicate(s) from ${originalCount} items (deduplicated by ${deduplicateBy})`
      // );
    }

    return uniqueItems.map((item) => ({ value: item.rowId, label: item.name }));
  };

  // Direct getters
  const getProductStatuses = () => getByType(PRODUCT_LOV_TYPES.PRODUCT_STATUS);
  const getProductTypes = () => getByType(PRODUCT_LOV_TYPES.PRODUCT_TYPE);
  const getProductCategories = () =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_CATEGORY);
  const getProductSubCategories = () =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_SUB_CATEGORY);
  const getProductColors = () =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_LINE_COLOUR);
  const getProductSizes = () => getByType(PRODUCT_LOV_TYPES.PRODUCT_SIZE);
  const getProductBrands = () => getByType(PRODUCT_LOV_TYPES.PRODUCT_BRAND);
  const getProductUOMs = () => getByType(PRODUCT_LOV_TYPES.PRODUCT_UOM);
  const getProductMarginTypes = () =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_MARGIN_TYPE);

  // Hierarchical helpers
  const getSubTypesForType = (typeRowId: number) =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_SUB_TYPE).filter(
      (item) => item.parRowId === typeRowId
    );

  const getSubCategoriesForCategory = (categoryRowId: number) =>
    getByType(PRODUCT_LOV_TYPES.PRODUCT_SUB_CATEGORY).filter(
      (item) => item.parRowId === categoryRowId
    );

  // Memoized option arrays
  const statusOptions = useMemo(
    () => createUniqueOptions(getProductStatuses(), 'Product Status'),
    [processedData]
  );

  const typeOptions = useMemo(
    () => createUniqueOptions(getProductTypes(), 'Product Type'),
    [processedData]
  );

  const categoryOptions = useMemo(
    () => createUniqueOptions(getProductCategories(), 'Product Category'),
    [processedData]
  );

  const colorOptions = useMemo(
    () => createUniqueOptions(getProductColors(), 'Product Color'),
    [processedData]
  );

  const sizeOptions = useMemo(
    () => createUniqueOptions(getProductSizes(), 'Product Size'),
    [processedData]
  );

  const brandOptions = useMemo(
    () => createUniqueOptions(getProductBrands(), 'Product Brand'),
    [processedData]
  );

  const uomOptions = useMemo(
    () => createUniqueOptions(getProductUOMs(), 'Product UOM'),
    [processedData]
  );

  const marginTypeOptions = useMemo(
    () => createUniqueOptions(getProductMarginTypes(), 'Product Margin Type'),
    [processedData]
  );

  return {
    isReady,
    isLoading,
    isProcessing,
    error,
    retry,

    // Direct getters
    getProductStatuses,
    getProductTypes,
    getProductCategories,
    getProductSubCategories,
    getProductColors,
    getProductSizes,
    getProductBrands,
    getProductUOMs,
    getProductMarginTypes,

    // Hierarchical helpers
    getSubTypesForType,
    getSubCategoriesForCategory,

    // Option arrays
    statusOptions,
    typeOptions,
    categoryOptions,
    colorOptions,
    sizeOptions,
    brandOptions,
    uomOptions,
    marginTypeOptions,
  };
};
